import {getHttpHeaders} from '@malt/http-client/src/header.utils';

const MAX_RETRIES = 5;
const INITIAL_RETRY_DELAY_10_SECOND = 10000;
const MAX_RETRY_DELAY_3_MIN = 3 * 60 * 1000;
const AUTOMATIC_RECONNECTION_INTERVAL = 2.5 * 60 * 1000;

interface SSEConfig {
    eventType: string;
    url: string;
    scope?: string;
}

export interface SSESubscription {
    unsubscribe: () => void;
    refresh: () => Promise<void>;
}

const UNAUTHENTICATED_STATUSES = [401, 403];

class SSEFetchError extends Error {
    readonly status: number;

    constructor(url: string, status: number) {
        super(`Failed to fetch ${url}: ${status}`);
        this.name = 'SSEFetchError';
        this.status = status;
    }
}

function logFetchFailure(message: string, error: unknown) {
    if (error instanceof SSEFetchError && UNAUTHENTICATED_STATUSES.includes(error.status)) {
        // User logged out or lost access to the resource: expected, nothing to report.
        return;
    }
    console.error(message, error);
}

function createSSESubscription<T>(config: SSEConfig, onMessage: (messageData: T) => void): SSESubscription {
    let eventSource: EventSource | null = null;
    let lastSseEventId: string | null = null;
    let initTriggered = false;
    let retryCount = 0;
    let reconnectionTimer: NodeJS.Timeout | null = null;
    let retryTimer: NodeJS.Timeout | null = null;

    function connectEventSource() {
        cleanupEventSourceIfAny();

        eventSource = new EventSource(`/sse/event-stream?types=${config.eventType}`);

        eventSource.onerror = handleError;
        eventSource.onopen = () => {
            retryCount = 0;
        };
        eventSource.addEventListener('init', handleInit);
        eventSource.addEventListener(config.eventType, handleMessage);
    }

    function cleanupEventSourceIfAny() {
        if (eventSource) {
            eventSource.close();
            eventSource = null;
        }
    }

    function clearRetryTimer() {
        if (retryTimer) {
            clearTimeout(retryTimer);
            retryTimer = null;
        }
    }

    function isStreamDead() {
        return eventSource === null && retryTimer === null;
    }

    function handleError(error: Event) {
        console.error('EventSource failed:', error);

        unsubscribe();

        if (retryCount < MAX_RETRIES) {
            retryCount++;
            const delay = calculateRetryDelay(retryCount);
            retryTimer = setTimeout(() => {
                retryTimer = null;
                connectEventSource();
            }, delay);
        }
    }

    async function fetchAndPublishCurrentState() {
        const allHeaders = getHttpHeaders();
        const headers: Record<string, string> = {};
        const immersionIdentity = allHeaders['X-Immersion-Identity'];
        if (immersionIdentity != null && immersionIdentity !== 'default') {
            headers['X-Immersion-Identity'] = immersionIdentity;
        }
        const response = await fetch(config.url, {headers});
        if (!response.ok) {
            throw new SSEFetchError(config.url, response.status);
        }
        const apiResult = await response.json();
        onMessage(apiResult);
    }

    async function handleInit(event: MessageEvent) {
        const data = JSON.parse(event.data);
        if (lastSseEventId && data[config.eventType] === lastSseEventId) {
            return;
        }
        if (!lastSseEventId && initTriggered) {
            return;
        }

        try {
            await fetchAndPublishCurrentState();
            initTriggered = true;
            lastSseEventId = data[config.eventType] || null;
        } catch (error) {
            logFetchFailure('Failed to fetch initial data:', error);
        }
    }

    /**
     * Publishes the current state again over the existing connection.
     * Lets callers force a refresh without opening a second event stream.
     * If the stream is dead (retries exhausted in handleError), reconnects first,
     * otherwise a long-lived worker would be stuck HTTP-refreshing forever.
     */
    async function refresh() {
        if (isStreamDead()) {
            retryCount = 0;
            subscribe();
        }

        try {
            await fetchAndPublishCurrentState();
        } catch (error) {
            logFetchFailure('Failed to refresh data:', error);
        }
    }

    function handleMessage(event: MessageEvent) {
        const data = JSON.parse(event.data);
        if (Object.keys(data).length === 0) {
            return;
        }

        lastSseEventId = event.lastEventId || null;
        onMessage(data);
    }

    function subscribe() {
        connectEventSource();

        if (reconnectionTimer) {
            clearInterval(reconnectionTimer);
            reconnectionTimer = null;
        }

        reconnectionTimer = setInterval(() => {
            // eslint-disable-next-line no-console
            console.debug('DEBUG - SSE : Reconnecting...');
            if (eventSource) {
                connectEventSource();
            }
        }, AUTOMATIC_RECONNECTION_INTERVAL);
    }

    function unsubscribe() {
        clearRetryTimer();
        cleanupEventSourceIfAny();

        if (reconnectionTimer) {
            clearInterval(reconnectionTimer);
            reconnectionTimer = null;
        }
    }

    subscribe();

    return {unsubscribe, refresh};
}

function calculateRetryDelay(retryCount: number): number {
    const exponentialDelay = Math.min(INITIAL_RETRY_DELAY_10_SECOND * Math.pow(2, retryCount - 1), MAX_RETRY_DELAY_3_MIN);
    const jitter = Math.random() * 0.5 + 0.5;
    const delay = Math.floor(exponentialDelay * jitter);

    // eslint-disable-next-line no-console
    console.debug('DEBUG - SSE : Time to retry : ', exponentialDelay, ' ; With jitter : ', delay);
    return delay;
}

export const sse = {
    subscribe: createSSESubscription,
};
